/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { Provider, connect } from 'react-redux';
import { Row, Col, Select, message } from 'antd';
import moment from 'moment';
import store from '../../store';
import Layout from '../../components/Layout/Layout';
import Button from '../../components/Button/Button';
import Download from '../../components/Download/Download';
import Theme from '../../components/Theme/Theme';
import { PointCardAnziif, PointCardIbanz, PointCardNiba } from './components/Card/Card';
import Certificate from './components/Certificate/Certificate';
import { download } from '../../components/App/AppActions';
import { fetchCurrentPeriodPoints, fetchLastPeriodPoints, updatePeriod } from './PointActions';
import { fetchSearchHistory } from '../Account/AccountActions';
import { fetchQuizzes } from '../Quiz/QuizActions';
import { periodPointsSelector, loadingPointsSelector } from './PointSelectors';
import { loadingSearchHistorySelector, countryCodeSelector } from '../Account/AccountSelectors';
import Cycle from './Cycle';
import './Point.scss';

const { Option } = Select;

function getCertificateCycle(certificate) {
  // Define cycles for each type
  const cpdCycle = new Cycle(moment().startOf('year'), moment().endOf('year'));
  const cipCycle = new Cycle(moment().startOf('year'), moment().endOf('year'));
  const cpdNzCycle = new Cycle(moment().startOf('year'), moment().endOf('year'));

  switch (certificate) {
    case 'cip':
      return cipCycle;
    case 'cpd':
      return cpdCycle;
    case 'cpdnz':
      return cpdNzCycle;
    default:
      return '';
  }
}

function getCertificateTitle(certificate) {
  switch (certificate) {
    case 'cip':
      return 'ANZIIF CIP Points';
    case 'cpd':
      return 'NIBA CPD Points';
    case 'cpdnz':
      return 'IBANZ CPD Points';
    default:
      return '';
  }
}

function getCertificateDescription(certificate) {
  switch (certificate) {
    case 'cip':
      return 'The Certified Insurance Professional (CIP) program requires that Institute CIP members complete and record a minimum of 25 CIP points of professional development activities each calendar year (January to December). Up to 15 points can be claimed from using LMI RiskCoach.';
    case 'cpd':
      return 'Brokers, insurance advisers and managers are required to keep well informed and up to date on the latest technical, product and regulatory changes affecting the professional by undertaking relevant continuing professional development (CPD). Like all professional bodies, NIBA requires all practising members to obtain a minimum of 25 CPD points per calendar year. Up to 10 points per year can be claimed from using LMI RiskCoach towards that requirement (daily limits apply).';
    case 'cpdnz':
      return 'Brokers, insurance advisers and managers are required to keep well informed and up to date on the latest technical, product and regulatory changes affecting the professional by undertaking relevant continuing professional development (CPD). Like all professional bodies, IBANZ requires all practising members to obtain a minimum of 10 CPD points each year. Up to 10 points per year can be claimed from using LMI RiskCoach.';
    default:
      return '';
  }
}

function getCertificateEligibility(certificate) {
  switch (certificate) {
    case 'cip':
      return 'LMI RiskCoach certifies the eligibility for 15 CIP points for online usage';
    case 'cpd':
      return 'LMI RiskCoach certifies the eligibility for 10 CPD points for online usage';
    case 'cpdnz':
      return 'LMI RiskCoach certifies the eligibility for 10 CPD points for online usage';
    default:
      return '';
  }
}

const Point = ({
  points = {},
  loading,
  country,
  download,
  fetchCurrentPeriodPoints,
  fetchLastPeriodPoints,
  updatePeriod,
  fetchSearchHistory,
  fetchQuizzes,
}) => {
  const parsedUrl = new URL(window.location);
  const type = parsedUrl.searchParams.get('type');
  const dateFormat = 'DD/MM/YYYY';

  // Initialize state based on the type
  const [certificate, setCertificate] = useState(type || 'cip');
  const [cycle, setCycle] = useState(getCertificateCycle(type || 'cip'));
  const [period, setPeriod] = useState('current');

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        await fetchSearchHistory({ start: cycle[period].startDate, end: cycle[period].endDate });
      } catch (err) {
        message.error(err.message);
      }
    };
    fetchHistory();
    fetchQuizzes({ status: 'completed', country });
    if (period === 'current') {
      fetchCurrentPeriodPoints();
    } else {
      fetchLastPeriodPoints({
        cipEnddate: getCertificateCycle('cip').last.endDate,
        cpdEnddate: getCertificateCycle('cpd').last.endDate,
        cpdnzEnddate: getCertificateCycle('cpdnz').last.endDate,
      });
    }
    updatePeriod({ start: cycle[period].startDate, end: cycle[period].endDate });
  }, [
    fetchQuizzes,
    fetchCurrentPeriodPoints,
    fetchLastPeriodPoints,
    fetchSearchHistory,
    updatePeriod,
    cycle,
    period,
    country,
  ]);

  const onDownloadCertificate = () => {
    const content = renderToStaticMarkup(
      <Provider store={store}>
        <Theme>
          <Certificate
            certificate={certificate}
            dateRange={[
              moment(cycle[period].startDate).format(dateFormat),
              moment(cycle[period].endDate).format(dateFormat),
            ]}
          />
        </Theme>
      </Provider>
    );
    return download(content);
  };

  const onCertificateChange = (selected) => {
    setCertificate(selected);
    setCycle(getCertificateCycle(selected));
  };

  const onPeriodChange = (selected) => {
    setPeriod(selected);
  };

  return (
    <Layout showMenu>
      <div className="point component-wrapper">
        <p>
          LMI RiskCoach subscribers are able to claim time spent using LMI RiskCoach as CIP or CPD credit points under
          current activity rules. Up to maximum 15 points claimable per annum. The CIP certificate can be printed off by
          selecting the period time.
        </p>
        <Row type="flex" gutter={40} className="point-card-list">
          <Col xs={24} sm={12} md={{ offset: 4, span: 8 }} className="point-card-list-item">
            <PointCardAnziif
              point={points.cip}
              active={certificate === 'cip'}
              mask
              onClick={() => onCertificateChange('cip')}
            />
          </Col>
          {country === 'nz' ? (
            <Col xs={24} sm={12} md={8} className="point-card-list-item">
              <PointCardIbanz
                point={points.cpdnz}
                active={certificate === 'cpdnz'}
                mask
                onClick={() => onCertificateChange('cpdnz')}
              />
            </Col>
          ) : (
            <Col xs={24} sm={12} md={8} className="point-card-list-item">
              <PointCardNiba
                point={points.cpd}
                active={certificate === 'cpd'}
                mask
                onClick={() => onCertificateChange('cpd')}
              />
            </Col>
          )}
        </Row>
        <h1 className="point-title">Get your Certificate</h1>
        <h2 className="point-subtitle">{getCertificateTitle(certificate)}</h2>
        <p>{getCertificateDescription(certificate)}</p>
        <Select
          placeholder="Select period"
          size="large"
          className="point-period"
          onChange={onPeriodChange}
          defaultValue="current"
        >
          <Option value="current">{`Current period: ${moment(cycle.current.startDate).format(dateFormat)} - ${moment(
            cycle.current.endDate
          ).format(dateFormat)}`}</Option>
          <Option value="last">{`Last period: ${moment(cycle.last.startDate).format(dateFormat)} - ${moment(
            cycle.last.endDate
          ).format(dateFormat)}`}</Option>
        </Select>
        <p>{getCertificateEligibility(certificate)}</p>
        <Download download={onDownloadCertificate} fileName="Certificate of Eligibility.pdf">
          {(onDownload, downloading) => (
            <Button
              type="primary"
              size="large"
              icon="download"
              loading={downloading}
              disabled={!cycle.current.startDate || !cycle.current.endDate || loading}
              onClick={onDownload}
            >
              Download
            </Button>
          )}
        </Download>
      </div>
    </Layout>
  );
};

const mapStateToProps = (state) => ({
  points: periodPointsSelector(state),
  loading: loadingPointsSelector(state) && loadingSearchHistorySelector(state),
  country: countryCodeSelector(state),
});

const mapDispatchToProps = {
  download,
  fetchCurrentPeriodPoints,
  updatePeriod,
  fetchLastPeriodPoints,
  fetchSearchHistory,
  fetchQuizzes,
};

export default connect(mapStateToProps, mapDispatchToProps)(Point);
